import React, { useState } from "react";

interface Entry {
  id: string;
  residentName: string;
  month: string;
  mood: number;
  engagement: number;
  social: number;
  appetite: number;
  pain: number;
  weightKg: string;
  goal: string;
  goalProgress: number;
  note: string;
  createdAt: string;
}

const KEY = "wellbeing_records";

function load(): Entry[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function loadResidents(): any[] {
  try {
    return JSON.parse(localStorage.getItem("residents_records") || "[]");
  } catch {
    return [];
  }
}

const scoreLabel = (n: number) => (n >= 5 ? "Excellent" : n >= 4 ? "Good" : n >= 3 ? "Fair" : "Low");

export default function Wellbeing({ user }: { user?: any }) {
  const [entries, setEntries] = useState<Entry[]>(load);
  const residents = loadResidents();
  const [form, setForm] = useState<Partial<Entry>>({ month: new Date().toISOString().slice(0, 7), mood: 3, engagement: 3, social: 3, appetite: 3, pain: 0 });
  const [message, setMessage] = useState("");

  const save = (next: Entry[]) => {
    setEntries(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const setNum = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }));

  const addEntry = () => {
    if (!form.residentName) {
      setMessage("Choose a resident first.");
      return;
    }
    const record: Entry = {
      id: `wb-${Date.now()}`,
      residentName: form.residentName || "",
      month: form.month || new Date().toISOString().slice(0, 7),
      mood: parseInt(String(form.mood), 10) || 3,
      engagement: parseInt(String(form.engagement), 10) || 3,
      social: parseInt(String(form.social), 10) || 3,
      appetite: parseInt(String(form.appetite), 10) || 3,
      pain: parseInt(String(form.pain), 10) || 0,
      weightKg: form.weightKg || "",
      goal: form.goal || "",
      goalProgress: parseInt(String(form.goalProgress), 10) || 0,
      note: form.note || "",
      createdAt: new Date().toISOString(),
    };
    save([record, ...entries]);
    setMessage(`Wellbeing review saved for ${record.residentName}.`);
  };

  const removeEntry = (id: string) => save(entries.filter(e => e.id !== id));

  const wellbeingOf = (e: Entry) => (e.mood + e.engagement + e.social + e.appetite) / 4;

  const names = Array.from(new Set(entries.map(e => e.residentName)));
  const trends = names.map(name => {
    const list = entries.filter(e => e.residentName === name).sort((a, b) => a.month.localeCompare(b.month));
    const last = list[list.length - 1];
    const prev = list[list.length - 2];
    const delta = prev ? wellbeingOf(last) - wellbeingOf(prev) : 0;
    return { name, last, delta };
  }).sort((a, b) => a.delta - b.delta);

  const improving = trends.filter(t => t.delta > 0.2).length;
  const declining = trends.filter(t => t.delta < -0.2).length;
  const stable = trends.length - improving - declining;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Outcome & Wellbeing Tracker</h2>
        <p className="text-sm text-slate-600">Monthly quality-of-life measures per resident with personal goals and improvement trends - the outcomes evidence inspectors look for.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{names.length}</div><div className="text-xs text-slate-500">Residents tracked</div></div>
        <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-center"><div className="text-2xl font-bold text-green-700">{improving}</div><div className="text-xs text-green-700">Improving</div></div>
        <div className="rounded-xl border border-slate-300 bg-slate-50 p-4 text-center"><div className="text-2xl font-bold text-slate-700">{stable}</div><div className="text-xs text-slate-700">Stable</div></div>
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-center"><div className="text-2xl font-bold text-red-700">{declining}</div><div className="text-xs text-red-700">Declining - act now</div></div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">New monthly review</h3>
        <div className="grid gap-3 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Resident</label>
            <select value={form.residentName || ""} onChange={e => setForm(f => ({ ...f, residentName: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option value="">Choose resident</option>
              {residents.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Month</label>
            <input type="month" value={form.month || ""} onChange={e => setForm(f => ({ ...f, month: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Weight (kg)</label>
            <input type="number" value={form.weightKg || ""} onChange={e => setForm(f => ({ ...f, weightKg: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Pain (0 none - 3 severe)</label>
            <select value={String(form.pain ?? 0)} onChange={e => setNum("pain", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option value="0">0 - None</option><option value="1">1 - Mild</option><option value="2">2 - Moderate</option><option value="3">3 - Severe</option>
            </select>
          </div>
          {["mood", "engagement", "social", "appetite"].map(key => (
            <div key={key}>
              <label className="mb-1 block text-xs font-medium text-slate-600 capitalize">{key} (1-5): {scoreLabel(parseInt(String(form[key as keyof Entry]), 10) || 3)}</label>
              <select value={String(form[key as keyof Entry] ?? 3)} onChange={e => setNum(key, e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                <option value="1">1 - Low</option><option value="2">2</option><option value="3">3 - Fair</option><option value="4">4 - Good</option><option value="5">5 - Excellent</option>
              </select>
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium text-slate-600">Personal goal (what a good day looks like)</label>
            <input value={form.goal || ""} onChange={e => setForm(f => ({ ...f, goal: e.target.value }))} placeholder="e.g. sit in the garden twice a week with her daughter" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Goal progress %</label>
            <input type="number" value={form.goalProgress ?? 0} onChange={e => setNum("goalProgress", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
          <div className="md:col-span-4">
            <label className="mb-1 block text-xs font-medium text-slate-600">Notes</label>
            <textarea value={form.note || ""} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
        </div>
        <button onClick={addEntry} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save review</button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Trends by resident</h3>
        <div className="space-y-2">
          {trends.map(t => (
            <div key={t.name} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3">
              <div className="text-sm font-medium text-slate-900">{t.name} <span className="text-xs text-slate-500">{t.last.month} | wellbeing {wellbeingOf(t.last).toFixed(1)}/5 | pain {t.last.pain}/3{t.last.weightKg ? ` | ${t.last.weightKg}kg` : ""}</span></div>
              <div className={`text-sm font-semibold ${t.delta > 0.2 ? "text-green-700" : t.delta < -0.2 ? "text-red-700" : "text-slate-600"}`}>
                {t.delta > 0.2 ? "▲ Improving" : t.delta < -0.2 ? "▼ Declining" : "● Stable"}
              </div>
            </div>
          ))}
          {trends.length === 0 ? <div className="text-sm text-slate-500">No reviews yet - save the first monthly review above.</div> : null}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Recent reviews</h3>
        <div className="space-y-2">
          {entries.slice(0, 10).map(e => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-2 text-sm">
              <div className="text-slate-800">{e.residentName} - {e.month} <span className="text-xs text-slate-500">mood {e.mood}, engagement {e.engagement}, social {e.social}, appetite {e.appetite}{e.goal ? ` | Goal: ${e.goal} (${e.goalProgress}%)` : ""}</span></div>
              <button onClick={() => removeEntry(e.id)} className="text-xs text-red-600">Remove</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}